'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import type Plyr from 'plyr';
import Hls from 'hls.js';
import 'plyr/dist/plyr.css';

import PlayerController from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/PlayerController';
import IconPlay from '@/context/global/components/Icon/IconPlay';
import IconPause from '@/context/global/components/Icon/IconPause';
import IconFullScreen from '@/context/global/components/Icon/IconFullScreen';
import { cn } from '@/context/global/utils/cn';
import { usePlaybackHeartbeat } from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/hooks/usePlaybackHeartbeat';

/**
 *  Controls auto-hide delay in milliseconds
 */
const CONTROLS_HIDE_DELAY = 2500;

/**
 *  Duration to show play/pause icon after toggle (in milliseconds)
 */
const PLAY_ICON_DISPLAY_DURATION = 1000;

const PREVIEW_START_TIME = 0;
const PREVIEW_DURATION = 30; // 30 seconds

const SEEK_STEP = 5; // seconds
const VOLUME_STEP = 0.05; // 5%

type PlayerProps = {
  src: string;
  songId?: number | null;
  title?: string;
  artist?: string;
  coverUrl?: string;
  disabledSeek?: boolean;
  disabledNext?: boolean;
  startTime?: number;
  onPrevious?: () => void;
  onNext?: () => void;
  onEnded?: () => void;
  onError?: () => void;
  onFavorite?: () => void;
  onMelody?: () => void;
  isFavorite?: boolean;
  hideCallingMelody?: boolean;
  isVocalOn: boolean;
  onVocalChange?: (value: boolean, currentTime: number) => void;
  volume: number;
  onVolumeChange: (value: number) => void;
  onSearchToggle?: () => void;
  isPreview?: boolean;
  onPreviewEnded?: () => void;
  onPlayingChange?: (isPlaying: boolean) => void;
};

const formatTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const Player = ({
  src,
  songId = null,
  title = 'Unknown Title',
  artist = 'Unknown Artist',
  disabledSeek = false,
  disabledNext = false,
  startTime = 0,
  onPrevious,
  onNext,
  onEnded,
  onError,
  onFavorite,
  onMelody,
  isVocalOn,
  onVocalChange,
  isFavorite = false,
  hideCallingMelody,
  volume,
  onVolumeChange,
  onSearchToggle,
  isPreview = false,
  onPreviewEnded,
  onPlayingChange,
}: PlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const plyrRef = useRef<Plyr | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isRepeat, setIsRepeat] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);
  const [isFakeFullscreen, setIsFakeFullscreen] = useState(false);

  // Detect mobile phone in landscape (height < 500px = phone, not tablet)
  useEffect(() => {
    const mql = window.matchMedia('(orientation: landscape) and (max-height: 500px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobileLandscape(e.matches);
    };
    handler(mql);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Video dimension validation timeout
  const dimensionCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // preview mode: track accumulated playback time
  const previewPlayTimeRef = useRef(0);
  const lastTimeUpdateRef = useRef(0);
  const previewEndedRef = useRef(false);
  const isPreviewRef = useRef(isPreview);
  const onPreviewEndedRef = useRef(onPreviewEnded);

  // Keep refs in sync with props
  useEffect(() => {
    isPreviewRef.current = isPreview;
    onPreviewEndedRef.current = onPreviewEnded;
  }, [isPreview, onPreviewEnded]);

  // Heartbeat for tracking playback time
  usePlaybackHeartbeat({
    isPlaying,
    songId,
  });

  // Preview mode: reset refs when src changes (new song)
  useEffect(() => {
    previewPlayTimeRef.current = 0;
    lastTimeUpdateRef.current = 0;
    previewEndedRef.current = false;
  }, [src]);

  const togglePlay = useCallback(() => {
    if (plyrRef.current) {
      if (isPlaying) {
        plyrRef.current.pause();
      } else {
        plyrRef.current.play();
      }
      // Show play/pause icon briefly
      setShowPlayIcon(true);
      setTimeout(() => setShowPlayIcon(false), PLAY_ICON_DISPLAY_DURATION);
    }
  }, [isPlaying]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || !video.duration || !isFinite(video.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * video.duration;
    video.currentTime = newTime;
  }, []);

  const toggleRepeat = useCallback(() => {
    setIsRepeat((prev) => !prev);
    if (videoRef.current) {
      videoRef.current.loop = !isRepeat;
    }
  }, [isRepeat]);

  const handlePrevious = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    onPrevious?.();
  }, [onPrevious]);

  const toggleVocal = useCallback(() => {
    const currentVideoTime = videoRef.current?.currentTime ?? 0;
    onVocalChange?.(!isVocalOn, currentVideoTime);
  }, [isVocalOn, onVocalChange]);

  const handleFullscreen = useCallback(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;

    // Check if already in fullscreen
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).webkitFullscreenElement ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).mozFullScreenElement ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).msFullscreenElement
    );

    // Exit fullscreen (native or fake)
    if (isFakeFullscreen) {
      setIsFakeFullscreen(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (screen.orientation as any)?.unlock?.();
      return;
    }
    if (isCurrentlyFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if ((document as any).webkitExitFullscreen) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document as any).webkitExitFullscreen();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if ((document as any).mozCancelFullScreen) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document as any).mozCancelFullScreen();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if ((document as any).msExitFullscreen) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document as any).msExitFullscreen();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (screen.orientation as any)?.unlock?.();
      return;
    }

    // Enter fullscreen
    // Standard + Vendor Prefixes (Android/Desktop)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestFs: any =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).requestFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).webkitRequestFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).mozRequestFullScreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).msRequestFullscreen;

    if (requestFs) {
      requestFs
        .call(container)
        .then(() => {
          // Lock orientation to landscape on Android/Desktop
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (screen.orientation as any)?.lock?.('landscape').catch(() => {
            // Orientation lock may fail, but that's okay
          });
        })
        .catch(() => {
          // Native fullscreen failed (iOS Safari) — use fake fullscreen
          setIsFakeFullscreen(true);
        });
    } else {
      // No Fullscreen API — use fake fullscreen
      setIsFakeFullscreen(true);
    }
  }, [isFakeFullscreen]);

  // Handle user activity to show/hide controls
  const handleUserActivity = useCallback(() => {
    setHasUserInteracted(true);
    setShowControls(true);

    // Clear existing timer
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // Start new timer to hide controls (only when playing)
    hideTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, CONTROLS_HIDE_DELAY);
  }, [isPlaying]);

  // Handle keyboard controls (only when focused)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isFocused) return;

      switch (e.key) {
        case ' ': // Spacebar - toggle play/pause
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft': // Seek backward
          e.preventDefault();
          if (!disabledSeek && videoRef.current) {
            videoRef.current.currentTime = Math.max(
              0,
              videoRef.current.currentTime - SEEK_STEP,
            );
          }
          break;
        case 'ArrowRight': // Seek forward
          e.preventDefault();
          if (!disabledSeek && videoRef.current) {
            videoRef.current.currentTime = Math.min(
              videoRef.current.duration || 0,
              videoRef.current.currentTime + SEEK_STEP,
            );
          }
          break;
        case 'ArrowUp': // Volume up
          e.preventDefault();
          onVolumeChange(Math.min(1, volume + VOLUME_STEP));
          break;
        case 'ArrowDown': // Volume down
          e.preventDefault();
          onVolumeChange(Math.max(0, volume - VOLUME_STEP));
          break;
      }
    },
    [isFocused, togglePlay, disabledSeek, volume, onVolumeChange],
  );

  // Cleanup hide timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Show controls after 3 seconds on first load (before user interacts)
  useEffect(() => {
    if (!hasUserInteracted) {
      const timer = setTimeout(() => {
        setShowControls(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [hasUserInteracted]);

  // Start hide timer when video starts playing
  useEffect(() => {
    if (isPlaying) {
      handleUserActivity();
    } else if (hasUserInteracted) {
      setShowControls(true);
    }
  }, [isPlaying, handleUserActivity, hasUserInteracted]);

  // Sync volume with video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  // Fullscreen event listeners
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = !!(
        document.fullscreenElement ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document as any).webkitFullscreenElement ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document as any).mozFullScreenElement ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document as any).msFullscreenElement
      );

      if (isFs) {
        // Entered fullscreen - lock orientation to landscape
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (screen.orientation as any)?.lock?.('landscape').catch(() => {});
      } else {
        // Exited fullscreen - unlock orientation
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (screen.orientation as any)?.unlock?.();
      }
    };

    // Add listeners for all vendor-prefixed fullscreen events
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !src) return;

    // Cleanup previous instances
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (plyrRef.current) {
      plyrRef.current.destroy();
      plyrRef.current = null;
    }

    // Add event listeners first (before HLS loads)
    const handlePlay = () => {
      setIsPlaying(true);
      onPlayingChange?.(true);
    };
    const handlePause = () => {
      setIsPlaying(false);
      onPlayingChange?.(false);
    };
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);

      // Preview mode: track playback time
      if (isPreviewRef.current && !previewEndedRef.current) {
        const now = video.currentTime;
        const delta = now - lastTimeUpdateRef.current;

        // Only count positive deltas (not seeks backward) and reasonable intervals
        if (delta > 0 && delta < 1) {
          previewPlayTimeRef.current += delta;
        }
        lastTimeUpdateRef.current = now;

        // Check if preview duration exceeded
        if (previewPlayTimeRef.current >= PREVIEW_DURATION) {
          previewEndedRef.current = true;
          onPreviewEndedRef.current?.();
        }
      }
    };
    const handleDuration = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    const handleEnded = () => {
      onEnded?.();
    };

    const handleVideoError = () => {
      console.error('Video error:', video.error);
      onError?.();
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleDuration);
    video.addEventListener('durationchange', handleDuration);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleVideoError);

    const initPlayer = async () => {
      // รอให้ video มี dimensions ก่อน init Plyr
      // แก้ไข: ใช้หลาย events + polling แทน loadedmetadata ซ้อนกัน 2 ชั้น
      // โค้ดเดิม loadedmetadata fire แค่ครั้งเดียว ถ้า videoWidth ยังเป็น 0 ตอน fire → player ค้าง
      await new Promise<void>((resolve) => {
        let resolved = false;

        const done = () => {
          if (resolved) return;
          resolved = true;
          clearInterval(pollInterval);
          video.removeEventListener('loadedmetadata', check);
          video.removeEventListener('loadeddata', check);
          video.removeEventListener('canplay', check);
          resolve();
        };

        const check = () => {
          if (video.videoWidth > 0 && video.videoHeight > 0) {
            done();
          }
        };

        // Check immediately
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          resolve();
          return;
        }

        // Listen to multiple events + poll as fallback to avoid race conditions
        video.addEventListener('loadedmetadata', check);
        video.addEventListener('loadeddata', check);
        video.addEventListener('canplay', check);
        const pollInterval = setInterval(check, 100);
      });

      const PlyrModule = await import('plyr');
      const PlyrClass = PlyrModule.default;

      // Initialize Plyr with no controls (CSS imported at top of file)
      plyrRef.current = new PlyrClass(video, {
        controls: [],
        clickToPlay: false,
        keyboard: { focused: false, global: false },
      });

      console.log(
        '[Player] Plyr initialized. Video element:',
        video,
        'Container:',
        plyrRef.current?.elements?.container,
      );

      // Set up dimension check after Plyr initialization
      dimensionCheckTimeoutRef.current = setTimeout(() => {
        const videoRect = video.getBoundingClientRect();

        if (videoRect.width === 0 || videoRect.height === 0) {
          console.error(
            '[Player] ERROR: Video element has zero dimensions after 3 seconds. Attempting reload...',
          );
          // Trigger error callback to reload
          onError?.();
        }
      }, 3000);

      // Check if duration is already available
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }

      // Seek to startTime if provided (for vocal toggle resume)
      // In preview mode: use startTime if provided (vocal toggle), else PREVIEW_START_TIME
      const actualStartTime = isPreviewRef.current
        ? startTime > 0
          ? startTime
          : PREVIEW_START_TIME
        : startTime;
      if (actualStartTime > 0) {
        video.currentTime = actualStartTime;
        lastTimeUpdateRef.current = actualStartTime;
      }

      // Auto-play when loaded
      try {
        await video.play();
      } catch (error) {
        if (error instanceof Error && error.name === 'NotAllowedError') {
          // (แก้ไข) Browser blocked autoplay with sound  เล่น muted ก่อน
          try {
            video.muted = true;
            await video.play();

            const unmute = () => {
              video.muted = false;
              document.removeEventListener('click', unmute);
              document.removeEventListener('touchstart', unmute);
            };
            document.addEventListener('click', unmute, { once: true });
            document.addEventListener('touchstart', unmute, { once: true });
          } catch {
            // ยัง play ไม่ได้  ปล่อยให้ user กดเอง
          }
        } else {
          throw error;
        }
      }
    };

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;

      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        initPlayer();
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data);
          onError?.();
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = src;
      video.addEventListener('loadedmetadata', () => {
        initPlayer();
      });
    }

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleDuration);
      video.removeEventListener('durationchange', handleDuration);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleVideoError);

      // Clear dimension check timeout
      if (dimensionCheckTimeoutRef.current) {
        clearTimeout(dimensionCheckTimeoutRef.current);
        dimensionCheckTimeoutRef.current = null;
      }

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (plyrRef.current) {
        plyrRef.current.destroy();
        plyrRef.current = null;
      }
    };
  }, [src]);

  // Preview mode: progress bar + เวลาแสดงแค่ 30 วิ
  const displayDuration = isPreview ? PREVIEW_DURATION : duration;
  const displayCurrentTime = isPreview ? previewPlayTimeRef.current : currentTime;
  const progress =
    displayDuration > 0
      ? (Math.min(displayCurrentTime, displayDuration) / displayDuration) * 100
      : 0;

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={cn(
        'relative h-full w-full bg-black outline-none',
        isFakeFullscreen && 'fake-fullscreen fixed inset-0 z-[9999] h-[100dvh] w-screen',
      )}
      onMouseMove={handleUserActivity}
      onMouseEnter={handleUserActivity}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      {/* Override Plyr container styles to fill parent */}
      <style jsx global>{`
        .plyr {
          position: relative !important;
          width: 100%;
          height: 100%;
        }
        .plyr__video-wrapper {
          position: relative !important;
          width: 100%;
          height: 100%;
        }
        .plyr video {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        /* Fullscreen mode styles */
        .plyr:fullscreen,
        .plyr:-webkit-full-screen,
        .plyr:-moz-full-screen,
        .plyr:-ms-fullscreen {
          width: 100vw !important;
          height: 100vh !important;
          max-width: 100vw !important;
          max-height: 100vh !important;
        }

        .plyr:fullscreen video,
        .plyr:-webkit-full-screen video,
        .plyr:-moz-full-screen video,
        .plyr:-ms-fullscreen video {
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
        }

        /* Fullscreen backdrop */
        ::backdrop {
          background-color: #000;
        }
        ::-webkit-backdrop {
          background-color: #000;
        }

        /* Fake fullscreen for iOS */
        .fake-fullscreen .plyr {
          width: 100vw !important;
          height: 100dvh !important;
        }
        .fake-fullscreen .plyr video {
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
        }
      `}</style>

      {/* Video Element */}
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-contain"
        style={{ minWidth: '100px', minHeight: '100px' }}
        playsInline
      />

      {/* Clickable overlay for play/pause toggle (YouTube style) - Desktop only */}
      <div
        className={cn(
          'absolute inset-0 hidden cursor-pointer md:block',
          isMobileLandscape && '!hidden',
        )}
        onClick={() => {
          handleUserActivity();
          if (!(isPreview && isPlaying)) togglePlay();
        }}
      />

      {/* Center Play/Pause Icon - Desktop only */}
      <div
        className={cn(
          'pointer-events-none absolute left-1/2 top-1/2 hidden size-[150px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#00000066] transition-opacity duration-300 md:flex',
          isMobileLandscape && '!hidden',
          showPlayIcon ? 'opacity-100' : 'opacity-0',
        )}
      >
        {isPlaying ? (
          <IconPause size={80} className="text-white" />
        ) : (
          <IconPlay size={80} className="text-white" />
        )}
      </div>

      {/* Mobile: Tap overlay - always active, refreshes hide timer or shows controls */}
      <div
        className={cn('absolute inset-0 z-10 md:hidden', isMobileLandscape && '!block')}
        onClick={handleUserActivity}
      />

      {/* Mobile Controls Overlay - pointer-events-none on container, only buttons are clickable */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 z-20 transition-opacity duration-300 md:hidden',
          (isMobileLandscape || isFakeFullscreen) && '!block',
          showControls ? 'opacity-100' : 'opacity-0',
        )}
      >
        {/* Mobile: Center Playback Controls */}
        <PlayerController
          isPreview={isPreview}
          title={title}
          artist={artist}
          isPlaying={isPlaying}
          isRepeat={isRepeat}
          isFavorite={isFavorite}
          isVocalOn={isVocalOn}
          volume={volume}
          hideCallingMelody={hideCallingMelody}
          disabledNext={disabledNext}
          onPrevious={handlePrevious}
          onNext={onNext}
          onTogglePlay={togglePlay}
          onToggleRepeat={toggleRepeat}
          onToggleVocal={toggleVocal}
          onVolumeChange={onVolumeChange}
          onFavorite={onFavorite}
          onMelody={onMelody}
          onToggleSearch={() => {
            if (isFakeFullscreen) setIsFakeFullscreen(false);
            onSearchToggle?.();
          }}
          forceMobile={isMobileLandscape}
        />

        {/* Mobile: Bottom Progress Bar */}
        <div className={cn(
          'pointer-events-auto absolute left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 pb-4 pt-8',
          isFakeFullscreen && !isMobileLandscape ? 'bottom-40' : 'bottom-0',
        )}>
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'h-1 flex-1 overflow-hidden rounded-full bg-white/30',
                !disabledSeek && 'cursor-pointer',
              )}
              onClick={disabledSeek ? undefined : handleSeek}
            >
              <div
                className="h-full rounded-full bg-[#A3D13C] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <button onClick={handleFullscreen} className="p-1">
              <IconFullScreen size={16} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Bottom Controls */}
      <div
        className={cn(
          'absolute bottom-0 left-0 right-0 hidden bg-gradient-to-t from-black/80 to-transparent pb-5 pt-20 transition-opacity duration-300 md:block',
          isMobileLandscape && '!hidden',
          showControls ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        {/* Timeline - Desktop */}
        <div className="mx-8 mb-4 flex items-center gap-6">
          <span className="font-db-heavent min-w-[50px] text-right text-[26px] font-bold text-[#D9D9D9]">
            {formatTime(displayCurrentTime)}
          </span>
          <div
            className={cn(
              'h-3 flex-1 overflow-hidden rounded-full bg-gray-100',
              !disabledSeek && 'cursor-pointer',
            )}
            onClick={disabledSeek ? undefined : handleSeek}
          >
            <div
              className="h-full rounded-full bg-[#A3D13C] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="font-db-heavent min-w-[50px] text-[26px] font-bold text-[#D9D9D9]">
            {formatTime(displayDuration)}
          </span>
        </div>

        {/* Controls Row - Desktop */}
        <div className="relative">
          <PlayerController
            isPreview={isPreview}
            title={title}
            artist={artist}
            isPlaying={isPlaying}
            isRepeat={isRepeat}
            isFavorite={isFavorite}
            isVocalOn={isVocalOn}
            volume={volume}
            hideCallingMelody={hideCallingMelody}
            disabledNext={disabledNext}
            onPrevious={handlePrevious}
            onNext={onNext}
            onTogglePlay={togglePlay}
            onToggleRepeat={toggleRepeat}
            onToggleVocal={toggleVocal}
            onVolumeChange={onVolumeChange}
            onFavorite={onFavorite}
            onMelody={onMelody}
          />
          {/* Fullscreen Button - Desktop (positioned absolutely at right) */}
          {/* <button
            onClick={handleFullscreen}
            className="absolute bottom-0 right-8 p-2 transition-opacity hover:opacity-80"
            title="Full Screen"
          >
            <IconFullScreen size={32} className="text-white" />
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default Player;
